import React from 'react'
// libs
import { useRouter } from 'hooks/useRouter'
import { useMutation } from '@apollo/client/react'
// components
import { DetailsButton } from 'components/UI'
import ConfirmDeleteModal from 'components/Modals/ConfirmDeleteModal'
// views
import { DetailsButtonCSS } from 'components/CTAs/PlanObjectiveActionsMenu'
// icons
import ColorIcon from '@mui/icons-material/ColorLensOutlined'
import DeleteIcon from '@mui/icons-material/DeleteOutlineOutlined'
import BookmarkIcon from '@mui/icons-material/BookmarkBorderOutlined'
import UnarchiveIcon from '@mui/icons-material/Unarchive'
// types
import { Modals } from 'constants/index'
import { ISecurityProfilePermissions } from 'types/graphql'
// context
import { KPIContext } from 'context/index'
import { KPIPageContext } from 'containers/KPIs/Register/layers/API'
import { PlanEditPageContext } from 'containers/Plan/Edit/layers/API'
// graphql
import { PLAN_KPI_DELETE_MUTATION } from 'graphql/planKPIs/mutations'
import { PLAN_ITEM_SET_ARCHIVED_MUTATION } from 'graphql/plan/mutations'
// hooks
import { useSnackbar } from 'notistack'
import { usePlanItemColorPicker } from 'hooks/usePlanItemColorPicker'
import { useBookmarks, useModalData, useShowModal } from 'hooks'
// utils
import get from 'lodash/get'
import { handleError } from 'utils/errors/handlers'
import { getSnackbarCloseButton } from 'providers/SnackbarProvider/utils'

interface Props {
  hideParent: () => void
}

const PlanKPIActionsMenu: React.FC<Props> = ({ hideParent }) => {
  const router = useRouter()

  const { entity, refetch, update } = React.useContext<any>(KPIContext)
  const { refetch: planKPIListRefetch } = React.useContext(KPIPageContext)
  const { treeQueryRefetch } = React.useContext(PlanEditPageContext)

  const { kpiDeleteKPI, kpiArchiveKPI } = entity.currentPermissions as ISecurityProfilePermissions

  const [deleteMutation] = useMutation(PLAN_KPI_DELETE_MUTATION)
  const [archiveMutation] = useMutation(PLAN_ITEM_SET_ARCHIVED_MUTATION)

  const { enqueueSnackbar, closeSnackbar } = useSnackbar()

  const { showModal } = useShowModal({ modalType: Modals.CONFIRM_DELETE_KPI_MODAL })
  const { modalData } = useModalData<{ color?: string }>({ modalType: Modals.KPI_MINIMIZED_MODAL })

  const shouldArchive = !entity.archived && kpiArchiveKPI

  const createOrDeleteBookmark = useBookmarks()

  const handleDeleteClick = () => {
    if (get(entity, 'planItems.data').length > 1) {
      return enqueueSnackbar('Record can not be deleted as it is referenced by another record!', {
        variant: 'warning',
        action: (key) => getSnackbarCloseButton(key, closeSnackbar)
      })
    }

    const planItemId = get(entity, 'planItems.data[0].id')

    showModal({
      width: 'sm',
      handleConfirm: async () => {
        try {
          if (shouldArchive) {
            await archiveMutation({ variables: { id: planItemId, archived: true } })
          } else {
            await deleteMutation({ variables: { id: entity.id } })
          }

          await treeQueryRefetch?.()
          await planKPIListRefetch?.()

          hideParent()
        } catch (errors) {
          handleError(errors, 'PlanKPIActionsMenu.handleDeleteClick')
        }
      }
    })
  }

  const handleSetColorClick = usePlanItemColorPicker({ entity, update, defaultColor: modalData.color })

  const onCreateOrDeleteBookmark = async () => {
    const input = {
      planKPIId: entity.id
    }

    await createOrDeleteBookmark(input, entity.isBookmark)

    refetch()

    if (router.query.slug?.[0] === 'bookmarks') {
      planKPIListRefetch?.()
    }
  }

  const handleUnarchiveClick = async () => {
    const planItemId = get(entity, 'planItems.data[0].id')

    try {
      await archiveMutation({ variables: { id: planItemId, archived: false } })
      await treeQueryRefetch?.()
    } catch (errors) {
      handleError(errors, 'PlanKPIActionsMenu.handleUnarchiveClick')
    }
  }

  return (
    <>
      <DetailsButton styles={DetailsButtonCSS}>
        <span onClick={onCreateOrDeleteBookmark}>
          <BookmarkIcon />
          {entity.isBookmark ? 'Remove from bookmarks' : 'Add to bookmarks'}
        </span>
        <span onClick={handleSetColorClick}>
          <ColorIcon />
          Set Color
        </span>
        {kpiArchiveKPI && entity.archived && (
          <span onClick={handleUnarchiveClick}>
            <UnarchiveIcon />
            Unarchive
          </span>
        )}
        {(kpiArchiveKPI || kpiDeleteKPI) && (
          <span onClick={handleDeleteClick}>
            <DeleteIcon />
            {kpiArchiveKPI ? 'Archive' : 'Delete'}
          </span>
        )}
      </DetailsButton>
      <ConfirmDeleteModal
        header={shouldArchive ? `Archive ${entity.description}` : `Delete ${entity.description}`}
        modalType={Modals.CONFIRM_DELETE_KPI_MODAL}>
        {shouldArchive ? (
          'Are you sure you want to archive this plan indicator? This will also archive any sub-items.'
        ) : (
          <span>
            Are you sure you want to delete this plan indicator? This will also <b>delete</b> any sub-items.
          </span>
        )}
      </ConfirmDeleteModal>
    </>
  )
}

export default PlanKPIActionsMenu
